import { useRef, useState } from "react";
import { api } from "../api";
import { Upload, Trash2, ImageOff, ImagePlus, AlertTriangle } from "lucide-react";

// Spotify artwork guidelines — square, min 1400×1400, recommended 3000×3000.
const MIN_DIM = 1400;
const RECOMMENDED_DIM = 3000;

// Load a File into an Image so we can inspect its natural dimensions before
// pushing it up to the server. Rejects if the browser can't decode it.
function readImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve({ width: img.naturalWidth, height: img.naturalHeight }); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read image dimensions.")); };
    img.src = url;
  });
}

/**
 * Cover art uploader for released songs.
 *
 * RBAC rules (mirror what the backend enforces):
 *  - Editors can upload only when no cover art exists yet.
 *  - Admins can upload, replace, or remove.
 *  - Readers see the thumbnail only.
 */
export default function CoverArtUpload({ user, entryId, coverPath, onChange, size = 120 }) {
  const isAdmin = user?.role === "admin";
  const isEditor = user?.role === "editor";
  const canUpload = (isAdmin || isEditor) && (!coverPath || isAdmin);
  const canRemove = isAdmin && !!coverPath;

  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [warn, setWarn] = useState("");

  const currentUrl = api.coverArt.urlFor(coverPath);

  const pick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { setErr("Please choose an image file."); setWarn(""); return; }
    if (f.size > 5 * 1024 * 1024) { setErr("Cover art must be under 5 MB."); setWarn(""); return; }
    setErr(""); setWarn("");
    // Spotify guidelines: square, at least 1400×1400. Enforce here so the
    // uploader gets instant feedback instead of waiting for a server 400.
    let dims;
    try {
      dims = await readImageDimensions(f);
    } catch {
      setErr("That file doesn't look like a valid image."); return;
    }
    if (dims.width !== dims.height) {
      setErr(`Cover art must be square (1:1). This image is ${dims.width}×${dims.height}. Crop it to a square before uploading.`);
      return;
    }
    if (dims.width < MIN_DIM) {
      setErr(`Image is too small (${dims.width}×${dims.height}). Spotify requires at least ${MIN_DIM}×${MIN_DIM}. Please upload a higher-resolution version.`);
      return;
    }
    setBusy(true);
    try {
      const res = await api.coverArt.upload(entryId, f);
      const path = res.cover_art_url.replace(/^\/api\/files\/covers\//, "");
      onChange?.(path);
      if (res.below_recommended) {
        setWarn(`Uploaded at ${dims.width}×${dims.height}. Spotify recommends ${RECOMMENDED_DIM}×${RECOMMENDED_DIM} for the sharpest playback on all devices.`);
      }
    } catch (ex) {
      setErr(String(ex.message || ex));
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async () => {
    if (!window.confirm("Remove this cover art? This can't be undone from the UI.")) return;
    setBusy(true);
    try {
      await api.coverArt.remove(entryId);
      onChange?.("");
    } catch (ex) {
      setErr(String(ex.message || ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="cover-art-upload" style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
      {/* Thumbnail preview */}
      <div
        onClick={canUpload ? pick : undefined}
        style={{
          width: size, height: size, borderRadius: 12,
          background: "var(--panel2)",
          border: "1px dashed var(--border)",
          overflow: "hidden", position: "relative",
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: canUpload ? "pointer" : "default",
          flexShrink: 0,
        }}
      >
        {currentUrl ? (
          <img
            src={currentUrl}
            alt="Cover art"
            loading="lazy"
            data-testid="cover-art-thumb"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <div style={{ textAlign: "center", color: "var(--muted)", padding: 10 }}>
            <ImagePlus size={22} style={{ opacity: 0.6 }} />
            <div style={{ fontSize: 10, marginTop: 6, letterSpacing: "0.06em" }}>No cover art</div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
        {canUpload && (
          <button
            type="button"
            onClick={pick}
            disabled={busy}
            data-testid="cover-art-upload-btn"
            className="btn btn-primary"
            style={{ padding: "8px 12px", fontSize: 12, alignSelf: "flex-start" }}
          >
            <Upload size={12} /> {busy ? "Uploading…" : (coverPath ? "Replace" : "Upload cover art")}
          </button>
        )}
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            disabled={busy}
            data-testid="cover-art-remove-btn"
            className="btn"
            style={{ padding: "8px 12px", fontSize: 12, color: "var(--danger)", alignSelf: "flex-start" }}
          >
            <Trash2 size={12} /> Remove
          </button>
        )}
        {!canUpload && !canRemove && coverPath && (
          <div className="muted" style={{ fontSize: 11, lineHeight: 1.5 }}>
            Only an admin can change this cover art.
          </div>
        )}
        {!canUpload && !coverPath && (
          <div className="muted" style={{ fontSize: 11, lineHeight: 1.5, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <ImageOff size={12} /> No cover art uploaded yet.
          </div>
        )}
        <div className="muted" style={{ fontSize: 10.5, lineHeight: 1.55, maxWidth: 280 }}>
          <b style={{ color: "var(--text)" }}>Spotify artwork</b> · square 1:1 · min <b style={{ color: "var(--text)" }}>1400×1400</b> · recommended 3000×3000 · JPG / PNG / WEBP · up to 5 MB
        </div>
        {warn && (
          <div data-testid="cover-art-warning" style={{ color: "var(--warn)", fontSize: 11, marginTop: 4, lineHeight: 1.5, display: "flex", gap: 6, alignItems: "flex-start", maxWidth: 300 }}>
            <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 2 }} /> <span>{warn}</span>
          </div>
        )}
        {err && (
          <div data-testid="cover-art-error" style={{ color: "var(--danger)", fontSize: 11, marginTop: 4, lineHeight: 1.5, maxWidth: 300 }}>{err}</div>
        )}
      </div>

      <input ref={fileRef} type="file" accept="image/*" onChange={onFile} style={{ display: "none" }} data-testid="cover-art-input" />
    </div>
  );
}
